import * as React from 'react'
import { AdminIcon } from '@/modules/shared/components'

import type { Moment } from 'moment'

import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { OutputType } from 'app/constants/outputType'

import { ActiveDriversModal } from '@/modules/adminPanel/printout/ActiveDriversModal'
import { AuditModal } from '@/modules/adminPanel/printout/AuditModal'
import { CompanyDetailsModel } from 'app/models/companyDetailsModel'
import { UserModel } from 'app/models/userModel'
import { isAdmin, isPaymentAdmin } from 'app/services/local/roleService'
import { SynchronizationModal } from '@/modules/adminPanel/printout/SynchronizationModal'
import { GroupModel } from 'app/models/groupModel'
import { MessageModel } from 'app/models/messageModel'
import { InvoiceDataModal } from '@/modules/adminPanel/printout/InvoiceDataModal'

export type ReportsStateProps = {
  companiesBasic: CompanyDetailsModel[]
  companiesForGroups: CompanyDetailsModel[]
  users: UserModel[]
  groups: GroupModel[]
}

export type ReportsDispatchProps = {
  showMessage: (message: MessageModel) => void
  getCompaniesBasic: (
    tenantId?: string,
    includeDeleted?: boolean,
    includeChildren?: boolean,
  ) => Promise<Array<CompanyDetailsModel>>
  getCompaniesBasicForGroups: (
    ids: Array<string>,
    includeDeleted?: boolean,
  ) => Promise<Array<CompanyDetailsModel>>
  getGroups: (tenantId?: string) => Promise<Array<GroupModel>>
  getAvailableUsers: (companyId?: string) => Promise<Array<UserModel>>
  getActiveDriversReport: (
    companies: Array<string>,
    groups: Array<string>,
    months: Array<number>,
    year: number,
    outputType: OutputType,
    showDetails: boolean,
    showActiveness: boolean,
    showTimesheet: boolean,
    showBusinessTrip: boolean,
    showPrintout: boolean,
    showBasic: boolean,
    showPRO: boolean,
    showVPN: boolean,
    showUpdater: boolean,
    showCurrentlyActive: boolean,
  ) => any
  getAuditReport: (
    companies: Array<string>,
    groups: Array<string>,
    user: string,
    startDate: Moment,
    endDate: Moment,
    events: Array<string>,
    outputType: OutputType,
    sortBy: number,
  ) => any
  getSynchronizationReport: (
    companies: Array<string>,
    user: string,
    startDate: Moment,
    endDate: Moment,
    outputType: OutputType,
    sortBy: number,
    onlyUpdater: boolean,
  ) => any
  setExported: () => Promise<any>
  getCompanyDataReport: (
    companies: Array<string>,
    groups: Array<string>,
    months: Array<number>,
    year: number,
    outputType: OutputType,
    sortBy: number,
    onlyPro: boolean,
    onlyPackage: boolean,
    mergeSameCompanies: boolean,
    source: number,
    updatePaymentDate: boolean,
  ) => any
  getRealIncomeReport: (companies: Array<string>, period: Moment) => any
}

export type ReportsOwnProps = {
  currentCompanyId: string
}

type ReportsProps = ReportsStateProps & ReportsDispatchProps & ReportsOwnProps

/**
 * Zakładka RAPORTY panelu admina - funkcyjna.
 *
 * Wcześniej klasa z pięcioma flagami w stanie i pięcioma niemal identycznymi blokami
 * JSX. Teraz lista opisowa i jeden stan „który modal jest otwarty"; same modale
 * zostają bez zmian - to osobne ekrany z własnymi formularzami.
 */
type ReportKey =
  | 'audit'
  | 'activeDrivers'
  | 'synchronization'
  | 'invoicing'
  | 'realIncome'

const Reports: React.FC<ReportsProps> = (props) => {
  const { translate } = useLocalize()
  const [openModal, setOpenModal] = React.useState<ReportKey | null>(null)
  const close = () => setOpenModal(null)

  const entries: Array<{ key: ReportKey; label: string; visible: boolean }> = [
    {
      key: 'audit',
      label: 'administrationPanel-companies.generate-audit-report',
      visible: isAdmin(),
    },
    {
      key: 'activeDrivers',
      label: 'administrationPanel-companies.generate-active-drivers-report',
      visible: true,
    },
    {
      key: 'synchronization',
      label: 'administrationPanel-companies.generate-synchronization-report',
      visible: isAdmin(),
    },
    {
      key: 'invoicing',
      label: 'administrationPanel-companies.generate-invoicing-report',
      visible: isAdmin() || isPaymentAdmin(),
    },
    {
      key: 'realIncome',
      label: 'administrationPanel-companies.generate-real-invoicing-report',
      visible: isAdmin() || isPaymentAdmin(),
    },
  ]

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-bg-content-primary">
      <ul className="tw-m-0 tw-list-none tw-p-2">
        {entries
          .filter((entry) => entry.visible)
          .map((entry) => (
            <li key={entry.key}>
              <button
                type="button"
                onClick={() => setOpenModal(entry.key)}
                className="tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded tw-border-0 tw-bg-transparent tw-px-3 tw-py-2 tw-text-left tw-text-sm tw-text-content-base hover:tw-bg-base-200"
              >
                <AdminIcon
                  name="print"
                  title={translate('operation.get-pdf').toString()}
                />
                {translate(entry.label) as string}
              </button>
            </li>
          ))}
      </ul>

      {openModal === 'activeDrivers' && (
        <ActiveDriversModal
          currentCompanyId={props.currentCompanyId}
          companies={props.companiesBasic}
          groups={props.groups}
          getCompanies={props.getCompaniesBasic}
          getGroups={props.getGroups}
          getActiveDriversReport={props.getActiveDriversReport}
          onClose={close}
        />
      )}
      {openModal === 'audit' && (
        <AuditModal
          currentCompanyId={props.currentCompanyId}
          companies={props.companiesBasic}
          getCompanies={props.getCompaniesBasic}
          groups={props.groups}
          getGroups={props.getGroups}
          users={props.users}
          getAvailableUsers={props.getAvailableUsers}
          getAuditReport={props.getAuditReport}
          onClose={close}
        />
      )}
      {openModal === 'synchronization' && (
        <SynchronizationModal
          companies={props.companiesBasic}
          getCompanies={props.getCompaniesBasic}
          users={props.users}
          getAvailableUsers={props.getAvailableUsers}
          getSynchronizationReport={props.getSynchronizationReport}
          onClose={close}
        />
      )}
      {openModal === 'invoicing' && (
        <InvoiceDataModal
          realIncome={false}
          getRealIncomeReport={props.getRealIncomeReport}
          currentCompanyId={props.currentCompanyId}
          companies={props.companiesForGroups}
          groups={props.groups}
          getCompaniesForGroups={props.getCompaniesBasicForGroups}
          getGroups={props.getGroups}
          source={0}
          getCompanyDataReport={props.getCompanyDataReport}
          onClose={close}
          onFinish={close}
        />
      )}
      {openModal === 'realIncome' && (
        <InvoiceDataModal
          realIncome
          getRealIncomeReport={props.getRealIncomeReport}
          currentCompanyId={props.currentCompanyId}
          companies={props.companiesForGroups}
          groups={props.groups}
          getCompaniesForGroups={props.getCompaniesBasicForGroups}
          getGroups={props.getGroups}
          source={0}
          getCompanyDataReport={props.getCompanyDataReport}
          onClose={close}
          onFinish={close}
        />
      )}
    </div>
  )
}

export default Reports
